import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import Chip from '../../../components/Chip';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import { ErrorIcon, OpenExternalIcon } from '../../../components/icons';
import { HelpTrigger } from '../../../help';
import { formatDate, formatDateTime } from '../../../utils/dateUtils';
import AdminPageShell from '../../components/AdminPageShell';
import FieldList from '../../components/FieldList';
import SearchSelect, { SearchOption } from '../../components/SearchSelect';
import { searchActiveOrganizations, searchPersons } from '../../lib/pickers';
import { apiError, inputClasses, labelClasses } from '../elections/InitiativeDetailPage';

interface NameHistoryRow {
  name: string | null;
  type: string | null;
  first_seen: string | null;
  last_seen: string | null;
}

interface CuratedRecord {
  id: string;
  purpose: string | null;
  web_url: string | null;
  organization_id: string | null;
  organization_name: string | null;
  contact_person_id: string | null;
  contact_person_name: string | null;
  status: string | null;
  date_registered: string | null;
  date_terminated: string | null;
  registered_agent_person_id: string | null;
  registered_agent_name: string | null;
  designated_filing_agent_person_id: string | null;
  designated_filing_agent_name: string | null;
  physical_address: string | null;
  mailing_address: string | null;
  phone: string | null;
  fax: string | null;
  beginning_balance: string | number | null;
  updated_at: string | null;
  updated_by_name: string | null;
}

interface CommitteeDetail {
  imported: {
    co_id: string;
    name: string | null;
    committee_type: string | null;
    first_seen: string | null;
    last_seen: string | null;
    name_history: NameHistoryRow[];
  };
  curated: CuratedRecord | null;
  links: {
    measures: Array<{
      id: string;
      number: string | null;
      name: string | null;
      cycle: string | number | null;
      publication_status: string | null;
      status: string | null;
      side: 'support' | 'oppose' | 'neutral';
    }>;
    candidates: Array<{
      candidate_id: string;
      person_id: string;
      full_name: string | null;
      office_sought: string | null;
      district: string | null;
      election_year: number | null;
    }>;
  };
}

/** The editable text fields, in form order (PRD §5.3). Pickers are handled separately. */
const TEXT_FIELDS = [
  'purpose', 'web_url', 'status', 'date_registered', 'date_terminated',
  'physical_address', 'mailing_address', 'phone', 'fax', 'beginning_balance',
] as const;
type TextField = typeof TEXT_FIELDS[number];

const PICKER_FIELDS = [
  'organization_id', 'contact_person_id', 'registered_agent_person_id', 'designated_filing_agent_person_id',
] as const;
type PickerField = typeof PICKER_FIELDS[number];

type FormState = Record<TextField, string>;
type PickerState = Record<PickerField, SearchOption | null>;

const PURPOSE_MAX_LENGTH = 2000;

const textValue = (value: string | number | null | undefined) =>
  value === null || value === undefined ? '' : String(value);

const formFrom = (curated: CuratedRecord | null): FormState => ({
  purpose: textValue(curated?.purpose),
  web_url: textValue(curated?.web_url),
  status: textValue(curated?.status),
  date_registered: textValue(curated?.date_registered).slice(0, 10),
  date_terminated: textValue(curated?.date_terminated).slice(0, 10),
  physical_address: textValue(curated?.physical_address),
  mailing_address: textValue(curated?.mailing_address),
  phone: textValue(curated?.phone),
  fax: textValue(curated?.fax),
  beginning_balance: textValue(curated?.beginning_balance),
});

const option = (id: string | null | undefined, label: string | null | undefined): SearchOption | null =>
  id ? { id, label: label || id } : null;

const pickersFrom = (curated: CuratedRecord | null): PickerState => ({
  organization_id: option(curated?.organization_id, curated?.organization_name),
  contact_person_id: option(curated?.contact_person_id, curated?.contact_person_name),
  registered_agent_person_id: option(curated?.registered_agent_person_id, curated?.registered_agent_name),
  designated_filing_agent_person_id: option(
    curated?.designated_filing_agent_person_id, curated?.designated_filing_agent_name
  ),
});

/**
 * Only the fields that changed, so two admins editing different fields don't
 * overwrite each other (PRD §5.3). Blank is sent as null, which clears the field.
 */
export function changedFields(
  initialForm: FormState, form: FormState, initialPickers: PickerState, pickers: PickerState
): Record<string, string | null> {
  const changes: Record<string, string | null> = {};
  for (const field of TEXT_FIELDS) {
    if (form[field].trim() !== initialForm[field].trim()) changes[field] = form[field].trim() || null;
  }
  for (const field of PICKER_FIELDS) {
    if ((pickers[field]?.id ?? null) !== (initialPickers[field]?.id ?? null)) changes[field] = pickers[field]?.id ?? null;
  }
  return changes;
}

const SIDE_LABELS: Record<string, string> = { support: 'Supports', oppose: 'Opposes', neutral: 'Neutral' };
const SIDE_CHIPS: Record<string, 'success' | 'danger' | 'neutral'> = { support: 'success', oppose: 'danger', neutral: 'neutral' };

const TEXT_INPUTS: Array<{ field: Exclude<TextField, 'purpose'>; label: string; type: string; hint?: string }> = [
  { field: 'web_url', label: 'Website', type: 'url', hint: 'Starts with https:// or http://' },
  { field: 'status', label: 'Status', type: 'text' },
  { field: 'date_registered', label: 'Registered', type: 'date' },
  { field: 'date_terminated', label: 'Terminated', type: 'date' },
  { field: 'phone', label: 'Phone', type: 'text' },
  { field: 'fax', label: 'Fax', type: 'text' },
];

/**
 * One committee: what the TRACER import says (locked), the details staff add
 * (editable), and every measure and candidate that shows them.
 */
const CommitteeDetailPage: React.FC = () => {
  const { coId = '' } = useParams<{ coId: string }>();
  const queryClient = useQueryClient();
  const queryKey = ['admin-committees', 'detail', coId];

  const detail = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await api.get(`/admin/committees/${encodeURIComponent(coId)}`);
      return response.data.data as CommitteeDetail;
    },
    enabled: coId !== '',
    retry: (count, error: any) => error?.response?.status !== 404 && count < 2,
    // A refetch resets the form, so never refetch behind the admin's back: copying a
    // purpose from TRACER in another tab would otherwise wipe what they'd typed.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const initialForm = useMemo(() => formFrom(detail.data?.curated ?? null), [detail.data]);
  const initialPickers = useMemo(() => pickersFrom(detail.data?.curated ?? null), [detail.data]);
  const [form, setForm] = useState<FormState>(initialForm);
  const [pickers, setPickers] = useState<PickerState>(initialPickers);

  useEffect(() => {
    setForm(initialForm);
    setPickers(initialPickers);
  }, [initialForm, initialPickers]);

  const changes = changedFields(initialForm, form, initialPickers, pickers);
  const dirty = Object.keys(changes).length > 0;

  // Closing or reloading the tab with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const saveMutation = useMutation({
    mutationFn: () => api.put(`/admin/committees/${encodeURIComponent(coId)}`, changes),
    onSuccess: (response) => {
      toast.success('Committee saved');
      queryClient.setQueryData(queryKey, response.data.data);
      queryClient.invalidateQueries({ queryKey: ['admin-list', 'committees-registry'] });
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save committee')),
  });

  const setText = (field: TextField) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));
  const setPicker = (field: PickerField) => (value: SearchOption | null) =>
    setPickers((current) => ({ ...current, [field]: value }));

  if (detail.isLoading) {
    return (
      <AdminPageShell title="Committee" backUrl="/admin-next/committees">
        <LoadingSpinner />
      </AdminPageShell>
    );
  }

  if (detail.isError || !detail.data) {
    const missing = (detail.error as any)?.response?.status === 404;
    return (
      <AdminPageShell title="Committee" backUrl="/admin-next/committees">
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <ErrorIcon className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            {missing
              ? `TRACER committee ${coId} isn't in the campaign finance import. Committees appear after their first filing is imported.`
              : "Couldn't load this committee. Please try again."}
          </p>
          {!missing && (
            <Button variant="outline" size="sm" onClick={() => detail.refetch()}>
              Retry
            </Button>
          )}
        </div>
      </AdminPageShell>
    );
  }

  const { imported, curated, links } = detail.data;
  const formerNames = imported.name_history.filter((row) => row.name && row.name !== imported.name);
  const linkedCount = links.measures.length + links.candidates.length;
  // The dashboard by TRACER id (PRD §5.12) — a name prefill guesses and breaks on renames.
  const financeSearch = `/campaign-finance?co_id=${encodeURIComponent(coId)}`;
  const purposeTooLong = form.purpose.trim().length > PURPOSE_MAX_LENGTH;

  return (
    <AdminPageShell
      title={imported.name || coId}
      description={`TRACER ${imported.co_id}${imported.committee_type ? ` · ${imported.committee_type}` : ''}`}
      backUrl="/admin-next/committees"
      actions={<HelpTrigger contentKey="admin-committees" size="md" ariaLabel="Help for committees" />}
    >
      <div className="space-y-4">
        <section className="card p-5" aria-labelledby="committee-imported-heading">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <h2 id="committee-imported-heading" className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
              From the TRACER import
            </h2>
            <Link
              to={financeSearch}
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline dark:text-primary-400"
            >
              Open in Campaign Finance <OpenExternalIcon className="h-4 w-4" aria-hidden />
            </Link>
          </div>
          <p className="mb-4 text-xs text-text-tertiary dark:text-text-dark-tertiary">
            From the TRACER campaign finance import. Changes in TRACER appear here after the next import.
          </p>
          <FieldList
            fields={[
              { label: 'Name', value: imported.name || '—', wrap: 'words' },
              { label: 'TRACER ID', value: imported.co_id, copyValue: imported.co_id },
              { label: 'Type', value: imported.committee_type || '—', wrap: 'words' },
              { label: 'First filing', value: formatDate(imported.first_seen) },
              { label: 'Last filing', value: formatDate(imported.last_seen) },
              ...(formerNames.length > 0
                ? [{
                    label: 'Former names',
                    fullWidth: true,
                    wrap: 'words' as const,
                    value: (
                      <ul className="space-y-0.5">
                        {formerNames.map((row) => (
                          <li key={`${row.name}-${row.first_seen}`}>
                            {row.name}
                            <span className="text-text-tertiary dark:text-text-dark-tertiary">
                              {' '}({formatDate(row.first_seen)} – {formatDate(row.last_seen)})
                            </span>
                          </li>
                        ))}
                      </ul>
                    ),
                  }]
                : []),
            ]}
          />
        </section>

        <section className="card p-5" aria-labelledby="committee-curated-heading">
          <h2 id="committee-curated-heading" className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
            Added by Capitol Commons
          </h2>
          <p className="mb-4 text-xs text-text-tertiary dark:text-text-dark-tertiary">
            What the import doesn't carry. Saved once here, shown on every measure and candidate linked below.
            {curated?.updated_at && (
              <> Last saved {formatDateTime(curated.updated_at)}{curated.updated_by_name ? ` by ${curated.updated_by_name}` : ''}.</>
            )}
          </p>

          <div className="space-y-4">
            <div>
              <label htmlFor="committee-purpose" className={labelClasses}>Purpose statement</label>
              <textarea
                id="committee-purpose"
                rows={4}
                value={form.purpose}
                onChange={setText('purpose')}
                className={inputClasses}
                aria-describedby="committee-purpose-hint"
                aria-invalid={purposeTooLong}
              />
              <p id="committee-purpose-hint" className={`mt-1 text-xs ${purposeTooLong ? 'text-error-600 dark:text-error-400' : 'text-text-tertiary dark:text-text-dark-tertiary'}`}>
                Copy it exactly from the committee's TRACER registration. {form.purpose.trim().length}/{PURPOSE_MAX_LENGTH}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <span className={labelClasses}>Organization</span>
                <SearchSelect
                  placeholder="Search organizations…"
                  ariaLabel="Search for the committee's organization"
                  fetchOptions={searchActiveOrganizations}
                  selected={pickers.organization_id}
                  onSelect={setPicker('organization_id')}
                  onClear={() => setPicker('organization_id')(null)}
                />
              </div>
              <div>
                <span className={labelClasses}>Contact person</span>
                <SearchSelect
                  placeholder="Search people…"
                  ariaLabel="Search for the committee's contact person"
                  fetchOptions={searchPersons}
                  selected={pickers.contact_person_id}
                  onSelect={setPicker('contact_person_id')}
                  onClear={() => setPicker('contact_person_id')(null)}
                />
              </div>
              {TEXT_INPUTS.map((input) => (
                <div key={input.field}>
                  <label htmlFor={`committee-${input.field}`} className={labelClasses}>{input.label}</label>
                  <input
                    id={`committee-${input.field}`}
                    type={input.type}
                    value={form[input.field]}
                    onChange={setText(input.field)}
                    className={inputClasses}
                  />
                  {input.hint && (
                    <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">{input.hint}</p>
                  )}
                </div>
              ))}
              <div>
                <span className={labelClasses}>Registered agent</span>
                <SearchSelect
                  placeholder="Search people…"
                  ariaLabel="Search registered agent"
                  fetchOptions={searchPersons}
                  selected={pickers.registered_agent_person_id}
                  onSelect={setPicker('registered_agent_person_id')}
                  onClear={() => setPicker('registered_agent_person_id')(null)}
                />
              </div>
              <div>
                <span className={labelClasses}>Designated filing agent</span>
                <SearchSelect
                  placeholder="Search people…"
                  ariaLabel="Search designated filing agent"
                  fetchOptions={searchPersons}
                  selected={pickers.designated_filing_agent_person_id}
                  onSelect={setPicker('designated_filing_agent_person_id')}
                  onClear={() => setPicker('designated_filing_agent_person_id')(null)}
                />
              </div>
              <div>
                <label htmlFor="committee-physical_address" className={labelClasses}>Physical address</label>
                <textarea id="committee-physical_address" rows={2} value={form.physical_address}
                  onChange={setText('physical_address')} className={inputClasses} />
              </div>
              <div>
                <label htmlFor="committee-mailing_address" className={labelClasses}>Mailing address</label>
                <textarea id="committee-mailing_address" rows={2} value={form.mailing_address}
                  onChange={setText('mailing_address')} className={inputClasses} />
              </div>
              <div>
                <label htmlFor="committee-beginning_balance" className={labelClasses}>Beginning balance</label>
                <input id="committee-beginning_balance" type="number" step="0.01" inputMode="decimal"
                  value={form.beginning_balance} onChange={setText('beginning_balance')} className={inputClasses} />
                <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  Funds carried into the current election cycle. Blank means unknown, not $0.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-light pt-4 dark:border-border-dark">
              {dirty && (
                <span className="mr-auto text-xs text-warning-700 dark:text-warning-400" role="status">
                  Unsaved changes
                </span>
              )}
              <Button
                variant="secondary"
                size="sm"
                disabled={!dirty || saveMutation.isLoading}
                onClick={() => {
                  setForm(initialForm);
                  setPickers(initialPickers);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!dirty || purposeTooLong}
                loading={saveMutation.isLoading}
                onClick={() => saveMutation.mutate()}
              >
                Save
              </Button>
            </div>
          </div>
        </section>

        <section className="card p-5" aria-labelledby="committee-links-heading">
          <h2 id="committee-links-heading" className="mb-1 text-sm font-semibold text-text-primary dark:text-text-dark-primary">
            Linked to
          </h2>
          <p className="mb-3 text-xs text-text-tertiary dark:text-text-dark-tertiary">
            {linkedCount === 0
              ? 'Nothing links to this committee yet. Link it from a ballot measure\'s Sides tab or a candidate\'s Campaign tab.'
              : 'Everything here shows this committee\'s details as saved above.'}
          </p>
          {links.measures.length > 0 && (
            <div className="mb-3">
              <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary">
                Ballot measures
              </h3>
              <ul className="space-y-1">
                {links.measures.map((measure) => (
                  <li key={`${measure.id}-${measure.side}`} className="flex flex-wrap items-center gap-2 text-sm">
                    <Link
                      to={`/admin-next/elections/initiatives/${measure.id}`}
                      className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                    >
                      {[measure.number, measure.name].filter(Boolean).join(' ') || 'Untitled measure'}
                    </Link>
                    <Chip label={SIDE_LABELS[measure.side]} variant={SIDE_CHIPS[measure.side]} size="xs" />
                    <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                      {[measure.cycle, measure.publication_status, measure.status].filter(Boolean).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {links.candidates.length > 0 && (
            <div>
              <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary">
                Candidates
              </h3>
              <ul className="space-y-1">
                {links.candidates.map((candidate) => (
                  <li key={candidate.candidate_id} className="flex flex-wrap items-center gap-2 text-sm">
                    <Link
                      to={`/admin-next/people/${candidate.person_id}`}
                      className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                    >
                      {candidate.full_name || 'Unnamed candidate'}
                    </Link>
                    <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                      {[candidate.election_year, candidate.office_sought, candidate.district].filter(Boolean).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>
    </AdminPageShell>
  );
};

export default CommitteeDetailPage;
